import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bootstrap, PlatformProvider, usePlatform, type PlatformState } from '@cyntox/common/platform';
import { api } from '../api/client';
import type { AdminReference, DataCoverage, Facility } from '../api/types';

/* ---------------------------------------------------------------------------
   The signed-in person and their access come from the shared platform. This
   adds what Admin's screens need: the locations and departments, the
   scorecard's scope (to narrow the employee and schedule lists), and how far
   the imported data reaches.
   --------------------------------------------------------------------------- */

interface AppState extends PlatformState {
  reference: AdminReference;
  /** Departments named in the scorecard's Scorecard Scope; all of them when unset. */
  scopedDepartments: string[];
  /** Facilities named in the scorecard's Scorecard Scope; all of them when unset. */
  scopedFacilities: Facility[];
  /** How far the imported data reaches. Undefined until the first load answers. */
  coverage?: DataCoverage;
}

const Ctx = createContext<AppState | null>(null);

export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used inside <AppProvider>');
  return v;
}

export function AppProvider({ children }: { children: ReactNode }) {
  return (
    <PlatformProvider appLabel="Admin">
      <AdminProvider>{children}</AdminProvider>
    </PlatformProvider>
  );
}

function AdminProvider({ children }: { children: ReactNode }) {
  const platform = usePlatform();
  const refQuery = useQuery({
    queryKey: ['reference'],
    queryFn: () => api.get<AdminReference>('/api/reference'),
  });
  const coverageQuery = useQuery({
    queryKey: ['data-coverage'],
    queryFn: () => api.get<DataCoverage>('/api/data-coverage'),
    enabled: refQuery.isSuccess,
  });

  const value = useMemo<AppState | null>(() => {
    const reference = refQuery.data;
    if (!reference) return null;

    // Filter dropdowns should only offer what could actually be listed, or you
    // can pick a department and get an empty table with no explanation. Editor
    // dropdowns keep using reference.departments / reference.facilities, because
    // moving somebody INTO scope means choosing a value that is not in it yet.
    const named = (key: string) =>
      (reference.settings?.[key] ?? '').split(',').map((x) => x.trim()).filter(Boolean);

    const namedDepartments = named('ScorecardDepartments');
    const namedLocations = named('ScorecardLocations');

    return {
      ...platform,
      reference,
      scopedDepartments: namedDepartments.length
        ? reference.departments.filter((d) => namedDepartments.some((n) => n.toLowerCase() === d.toLowerCase()))
        : reference.departments,
      scopedFacilities: namedLocations.length
        ? reference.facilities.filter((f) => namedLocations.some((n) => n.toLowerCase() === f.code.toLowerCase()))
        : reference.facilities,
      coverage: coverageQuery.data,
    };
  }, [platform, refQuery.data, coverageQuery.data]);

  // A failed load would otherwise leave "Loading…" up forever with no reason.
  if (refQuery.isError) {
    return (
      <Bootstrap title="Could not load Admin">
        <p>{(refQuery.error as Error).message}</p>
      </Bootstrap>
    );
  }
  if (!value) return <Bootstrap title="Loading Admin…">{null}</Bootstrap>;

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
